import { type ImageOptions } from '@backend/lib/db/typeRoots/layoutNode'
import { Tooltip } from '@radix-ui/react-tooltip'
import { TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/shadcn/tooltip'
import { PopoverMenu } from './common/popover-menu'
import { ToggleMenu } from '@/components/ui/ToggleMenu'
import { WorkspaceEditorMenuWrapper } from './workspace-editor-menu-wrapper'
import { ColorPickerQuickmenu } from '@/components/ui/ColorPicker'
import { SeparatorQuickmenu } from '@/components/ui/SeparatorQuickmenu'
import {
  BorderIcon,
  BringForwardIcon,
  Delete1Icon,
  LayersIcon,
  MagicIcon,
  OpacityIcon,
  SendBackwardIcon,
  SendToBackIcon,
  SendToFrontIcon,
  ShadowIcon,
  StarsOutlineIcon,
  UploadIcon,
} from '@/components/ui/icons/icons-new'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/shadcn/dropdown-menu'
import { ImageIcon } from 'lucide-react'
import { NumberInput } from './common/number-input'
import { DownloadIconNew } from '@/components/ui/icons/icons-new/download-icon-new'
import { LockToggleButton } from './common/lock-toggle-button'

interface Props {
  options: ImageOptions
  onUpdate: (updates: Partial<ImageOptions>, isHoverSelection?: boolean) => void
  onLayerChange: (operation: 'bringToFront' | 'moveForward' | 'moveBackward' | 'sendToBack') => void
  onDelete: () => void
  onDuplicate: () => void
  onDownload: () => void
  onEditImage?: () => void
  onReplaceUpload?: () => void
  onReplaceGenerate?: () => void
  locked: boolean
  onToggleLock: () => void
  colorPalette: string[]
}

export function WorkspaceEditorMenuImage({
  options,
  onUpdate,
  onLayerChange,
  onDelete,
  onDuplicate,
  onDownload,
  onEditImage,
  onReplaceUpload,
  onReplaceGenerate,
  locked,
  onToggleLock,
  colorPalette,
}: Props) {
  return (
    <WorkspaceEditorMenuWrapper>
      <TooltipProvider delayDuration={300}>
        {/* mode */}
        <PopoverMenu
          trigger={options.mode === 'distort' ? 'fill' : options.mode}
          hoverContent="Change fill type"
          options={[
            {
              label: '',
              content: (
                <ToggleMenu
                  type="single"
                  selectedKey={options.mode}
                  options={[
                    { key: 'contain', label: 'contain' },
                    { key: 'cover', label: 'cover' },
                    { key: 'distort', label: 'fill' },
                  ]}
                  onClick={(selectedKey) =>
                    onUpdate({ mode: selectedKey as 'contain' | 'cover' | 'distort' })
                  }
                />
              ),
            },
          ]}
        />
        {/* border */}
        <PopoverMenu
          trigger={<BorderIcon width={22} height={22} color="var(--foreground)" />}
          hoverContent="Stroke width, radius, color"
          options={[
            {
              label: 'stroke width',
              content: (
                <NumberInput
                  value={options.borderWidth}
                  onChange={(selectedKey, isHoverSelection) =>
                    onUpdate({ borderWidth: selectedKey }, isHoverSelection)
                  }
                  onBlur={(selectedKey) => onUpdate({ borderWidth: selectedKey })}
                  min={0}
                  max={9999}
                  step={1}
                />
              ),
            },
            {
              label: 'rounded corners',
              content: (
                <NumberInput
                  value={options.borderRadius}
                  onChange={(selectedKey, isHoverSelection) =>
                    onUpdate({ borderRadius: selectedKey }, isHoverSelection)
                  }
                  onBlur={(selectedKey) => onUpdate({ borderRadius: selectedKey })}
                  min={0}
                  max={9999}
                  step={1}
                />
              ),
            },
            {
              label: 'stroke color',
              content: (
                <ColorPickerQuickmenu
                  value={options.borderColor}
                  onChange={(selectedKey, isHoverSelection) => {
                    onUpdate({ borderColor: selectedKey }, isHoverSelection)
                  }}
                  colorPalette={colorPalette}
                />
              ),
            },
          ]}
        />
        {/* opacity */}
        <PopoverMenu
          trigger={<OpacityIcon width={22} height={22} color="var(--foreground)" />}
          hoverContent="Opacity"
          options={[
            {
              label: 'Opacity',
              content: (
                <NumberInput
                  value={Math.floor(options.opacity * 100)}
                  onChange={(value, isHoverSelection) =>
                    onUpdate({ opacity: Math.floor(value) / 100 }, isHoverSelection)
                  }
                  onBlur={(value) => onUpdate({ opacity: Math.floor(value) / 100 })}
                  min={0}
                  max={100}
                  step={1}
                />
              ),
            },
          ]}
        />

        <SeparatorQuickmenu />

        {/* Replace Image */}
        {(onReplaceUpload || onReplaceGenerate) && (
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <span className="hover:bg-hover flex cursor-pointer items-center justify-center rounded-sm p-1">
                    <ImageIcon className="h-[22px] w-[22px] text-[var(--foreground)]" />
                  </span>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent className="w-full">
                <p>Replace image</p>
              </TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="center" sideOffset={8}>
              {onReplaceUpload && (
                <DropdownMenuItem onClick={onReplaceUpload} className="gap-2">
                  <UploadIcon width={16} height={16} color="var(--foreground)" />
                  Upload
                </DropdownMenuItem>
              )}
              {onReplaceGenerate && (
                <DropdownMenuItem onClick={onReplaceGenerate} className="gap-2">
                  <MagicIcon width={16} height={16} color="var(--foreground)" />
                  Generate
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Edit Image with AI */}
        {onEditImage && (
          <Tooltip>
            <TooltipTrigger>
              <span
                className="hover:bg-hover flex cursor-pointer items-center justify-center rounded-sm p-1"
                onClick={onEditImage}>
                <StarsOutlineIcon width={22} height={22} color="var(--foreground)" />
              </span>
            </TooltipTrigger>
            <TooltipContent className="w-full">
              <p>Edit with AI</p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Layers */}
        <PopoverMenu
          trigger={<LayersIcon width={22} height={22} color="var(--foreground)" />}
          hoverContent="Layers"
          options={[
            {
              label: '',
              content: (
                <span
                  className="hover:bg-hover flex cursor-pointer items-center justify-start gap-2 rounded-sm p-1"
                  onClick={() => onLayerChange('sendToBack')}>
                  <SendToBackIcon width={22} height={22} color="var(--foreground)" />
                  Send to back
                </span>
              ),
            },
            {
              label: '',
              content: (
                <span
                  className="hover:bg-hover flex cursor-pointer items-center justify-start gap-2 rounded-sm p-1"
                  onClick={() => onLayerChange('moveBackward')}>
                  <SendBackwardIcon width={22} height={22} color="var(--foreground)" />
                  Send backward
                </span>
              ),
            },
            {
              label: '',
              content: (
                <span
                  className="hover:bg-hover flex cursor-pointer items-center justify-start gap-2 rounded-sm p-1"
                  onClick={() => onLayerChange('moveForward')}>
                  <BringForwardIcon width={22} height={22} color="var(--foreground)" />
                  Bring forward
                </span>
              ),
            },
            {
              label: '',
              content: (
                <span
                  className="hover:bg-hover flex cursor-pointer items-center justify-start gap-2 rounded-sm p-1"
                  onClick={() => onLayerChange('bringToFront')}>
                  <SendToFrontIcon width={22} height={22} color="var(--foreground)" />
                  Bring to front
                </span>
              ),
            },
          ]}
        />
        <Tooltip>
          <TooltipTrigger>
            <span
              className="hover:bg-hover flex cursor-pointer items-center justify-center rounded-sm p-1"
              onClick={onDownload}>
              <DownloadIconNew width={22} height={22} color="var(--foreground)" />
            </span>
          </TooltipTrigger>
          <TooltipContent className="w-full">
            <p>Download</p>
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger>
            <span
              className="hover:bg-hover flex cursor-pointer items-center justify-center rounded-sm p-1"
              onClick={onDuplicate}>
              <ShadowIcon width={22} height={22} color="var(--foreground)" />
            </span>
          </TooltipTrigger>
          <TooltipContent className="w-full">
            <p>Duplicate item</p>
          </TooltipContent>
        </Tooltip>
        <LockToggleButton locked={locked} onToggle={onToggleLock} />
        <Tooltip>
          <TooltipTrigger>
            <span
              className="hover:bg-hover flex cursor-pointer items-center justify-center rounded-sm p-1"
              onClick={onDelete}>
              <Delete1Icon width={22} height={22} color="var(--foreground)" />
            </span>
          </TooltipTrigger>
          <TooltipContent className="w-full">
            <p>Delete item</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </WorkspaceEditorMenuWrapper>
  )
}
